// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Monday Madness document. It reads like the email it becomes: heading, a short
// intro, summary tiles, one card per project, then the report sections and a sign-off.

import {useMondayChoices} from '@app/features/konvo_reports/components/MondayChoiceMenu';
import {AutoTextarea, MondayMarkdownField} from '@app/features/konvo_reports/components/MondayMarkdownField';
import {
	BlockActions,
	MondaySectionBlock,
	SectionKindIcon,
} from '@app/features/konvo_reports/components/MondaySectionBlock';
import {moveEntry, newSection, removeAt, replaceAt} from '@app/features/konvo_reports/utils/MondayDraftEdit';
import {
	formatMinutes,
	type MondayDraftProject,
	type MondayDraftSection,
	type MondayReportDraft,
	type MondaySectionKind,
	mondaySummary,
	projectMinutes,
} from '@fluxer/schema/src/domains/konvo/MondayReportDraft';
import {useLingui} from '@lingui/react/macro';
import {IconPlus} from '@tabler/icons-react';
import type React from 'react';
import styles from './MondayReportCard.module.css';

type Change = (change: Partial<MondayReportDraft>) => void;

function AddButton({
	label,
	onPick,
	choices,
	disabled,
}: {
	label: string;
	onPick: (kind: MondaySectionKind, title: string) => void;
	choices: Array<{kind: MondaySectionKind; title: string; label?: string}>;
	disabled?: boolean;
}) {
	const menu = useMondayChoices();
	const open = (event: React.MouseEvent<HTMLButtonElement>) =>
		menu.show(
			event,
			label,
			choices.map((choice) => ({
				key: `${choice.kind}:${choice.title}`,
				label: choice.label ?? choice.title,
				icon: <SectionKindIcon kind={choice.kind} />,
				onPick: () => onPick(choice.kind, choice.title),
			})),
		);
	return (
		<>
			<button type="button" className={styles.addBlock} disabled={disabled} onClick={open}>
				<IconPlus size={16} stroke={1.75} aria-hidden="true" />
				{label}
			</button>
			{menu.sheet}
		</>
	);
}

function ProjectBlock({
	project,
	onChange,
	onRemove,
	index,
	count,
	onMove,
	showHours,
}: {
	project: MondayDraftProject;
	onChange: (project: MondayDraftProject) => void;
	onRemove: () => void;
	index: number;
	count: number;
	onMove: (delta: number) => void;
	showHours: boolean;
}) {
	const {t} = useLingui();
	const minutes = projectMinutes(project);
	const setSections = (sections: Array<MondayDraftSection>) => onChange({...project, sections});
	return (
		<section className={styles.project} data-hidden={project.hidden || undefined} data-flx="konvo-monday.project">
			<div className={styles.blockHeading}>
				<input
					className={styles.projectName}
					aria-label={t`Project name`}
					value={project.name}
					maxLength={200}
					onChange={(event) => onChange({...project, name: event.target.value})}
				/>
				{project.hidden && <span className={styles.hiddenTag}>{t`Hidden`}</span>}
				{showHours && minutes > 0 && <span className={styles.count}>{formatMinutes(minutes)}</span>}
				<BlockActions
					moves={{index, count, onMove}}
					hidden={project.hidden}
					onToggleHidden={() => onChange({...project, hidden: !project.hidden})}
					onRemove={onRemove}
					what="project"
				/>
			</div>
			{project.sections.map((section, sectionIndex) => (
				<MondaySectionBlock
					key={section.id}
					section={section}
					level="project"
					showHours={showHours}
					onChange={(next) => setSections(replaceAt(project.sections, sectionIndex, next))}
					onRemove={() => setSections(removeAt(project.sections, sectionIndex))}
					moves={{
						index: sectionIndex,
						count: project.sections.length,
						onMove: (delta) => setSections(moveEntry(project.sections, sectionIndex, delta)),
					}}
				/>
			))}
			<AddButton
				label={t`Add to project`}
				disabled={project.sections.length >= 12}
				choices={[
					{kind: 'list', title: t`Tasks`, label: t`Task list`},
					{kind: 'text', title: t`Notes`, label: t`Note`},
					{kind: 'list', title: t`Next steps`},
					{kind: 'list', title: t`Risks`},
				]}
				onPick={(kind, title) => setSections([...project.sections, newSection(kind, title)])}
			/>
		</section>
	);
}

export function MondayReportEditor({draft, update}: {draft: MondayReportDraft; update: Change}) {
	const {t} = useLingui();
	const summary = draft.options.summary ? mondaySummary(draft) : [];
	const showHours = draft.options.hours;
	return (
		<div className={styles.document}>
			<div className={styles.eyebrow}>{t`Monday Madness`}</div>
			<AutoTextarea
				className={styles.heading}
				aria-label={t`Report heading`}
				value={draft.heading}
				maxLength={200}
				onKeyDown={(event) => event.key === 'Enter' && event.preventDefault()}
				onChange={(event) => update({heading: event.target.value.replace(/\n/g, ' ')})}
			/>
			<input
				className={styles.weekLabel}
				aria-label={t`Week`}
				value={draft.weekLabel}
				maxLength={100}
				onChange={(event) => update({weekLabel: event.target.value})}
			/>
			<MondayMarkdownField
				className={styles.intro}
				label={t`Introduction`}
				placeholder={t`Add a short introduction (optional)`}
				value={draft.intro}
				maxLength={4000}
				onChange={(intro) => update({intro})}
			/>
			{summary.length > 0 && (
				<div className={styles.summary} role="group" aria-label={t`Summary`}>
					{summary.map((tile) => (
						<div key={tile.label} className={styles.tile}>
							<strong>{tile.value}</strong>
							<span>{tile.label}</span>
						</div>
					))}
				</div>
			)}
			<div className={styles.groupHeading}>
				<h3>{t`Projects`}</h3>
				{draft.projects.length > 0 && <span className={styles.count}>{draft.projects.length}</span>}
			</div>
			{draft.projects.length === 0 && (
				<p className={styles.empty}>{t`No ClickUp tasks moved last week. Add a project to write about your work.`}</p>
			)}
			{draft.projects.map((project, index) => (
				<ProjectBlock
					key={project.id}
					project={project}
					index={index}
					count={draft.projects.length}
					showHours={showHours}
					onChange={(next) => update({projects: replaceAt(draft.projects, index, next)})}
					onRemove={() => update({projects: removeAt(draft.projects, index)})}
					onMove={(delta) => update({projects: moveEntry(draft.projects, index, delta)})}
				/>
			))}
			<button
				type="button"
				className={styles.addBlock}
				disabled={draft.projects.length >= 40}
				onClick={() =>
					update({
						projects: [
							...draft.projects,
							{
								id: crypto.randomUUID(),
								name: t`New project`,
								sections: [newSection('list', t`Tasks`)],
								hidden: false,
							},
						],
					})
				}
			>
				<IconPlus size={16} stroke={1.75} aria-hidden="true" />
				{t`Add project`}
			</button>
			{draft.sections.map((section, index) => (
				<MondaySectionBlock
					key={section.id}
					section={section}
					level="report"
					showHours={showHours}
					onChange={(next) => update({sections: replaceAt(draft.sections, index, next)})}
					onRemove={() => update({sections: removeAt(draft.sections, index)})}
					moves={{
						index,
						count: draft.sections.length,
						onMove: (delta) => update({sections: moveEntry(draft.sections, index, delta)}),
					}}
				/>
			))}
			<AddButton
				label={t`Add section`}
				disabled={draft.sections.length >= 20}
				choices={[
					{kind: 'text', title: t`New section`, label: t`Text`},
					{kind: 'list', title: t`New list`, label: t`List`},
					{kind: 'time', title: t`Time`, label: t`Time table`},
					{kind: 'list', title: t`Wins`},
					{kind: 'list', title: t`Next week`},
					{kind: 'text', title: t`Blockers`},
					{kind: 'list', title: t`Asks`},
				]}
				onPick={(kind, title) => update({sections: [...draft.sections, newSection(kind, title)]})}
			/>
			<MondayMarkdownField
				className={styles.signoff}
				label={t`Sign-off`}
				placeholder={t`Sign-off (optional)`}
				value={draft.signoff}
				maxLength={1000}
				onChange={(signoff) => update({signoff})}
			/>
			<div className={styles.sentFrom}>{t`Sent from Konvo`}</div>
		</div>
	);
}
